import React, { useState } from "react";
import { Link, useLocation } from "wouter";
import { Phone, Calendar, Clock, MapPin, Menu, X, ShieldCheck, ChevronRight, Star } from "lucide-react";
import { PRACTICE_INFO } from "@/site-data";

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [location] = useLocation();

  const navLinks: { title: string; href: string; external?: boolean }[] = [
    { title: "Home", href: "/" },
    { title: "About", href: "/about/" },
    { title: "Services", href: "/services/" },
    { title: "Swift", href: "/services/swift-microwave-therapy/" },
    { title: "Office", href: "/heber-office/" },
    { title: "Articles", href: "/articles/" },
    { title: "Forms", href: "/forms/" },
    { title: "Reviews", href: "https://search.google.com/local/writereview?placeid=ChIJWfBvY7P1TYcR1cbImu6-QFU", external: true },
    { title: "Contact", href: "/appointments/" },
  ];

  return (
    <header className="w-full bg-white border-b border-slate-200 sticky top-0 z-50">
      {/* Top Banner with phone and emergency note */}
      <div className="bg-[#0f2b48] text-white py-2 px-4 text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 text-slate-200">
              <MapPin className="w-3.5 h-3.5 text-cyan-400" />
              190 N. Main Street, Heber City, UT 84032
            </span>
            <span className="hidden md:inline-block text-slate-400">|</span>
            <span className="hidden md:flex items-center gap-1.5 text-slate-200">
              <Clock className="w-3.5 h-3.5 text-cyan-400" />
              Mon-Tue, Thu: 8am-5pm
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href={`tel:${PRACTICE_INFO.phone}`}
              className="font-bold flex items-center gap-1.5 text-cyan-300 hover:text-white transition-colors"
            >
              <Phone className="w-3.5 h-3.5" />
              Call Now: {PRACTICE_INFO.officePhone}
            </a>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#0f2b48] flex items-center justify-center text-cyan-300 font-bold text-xl shadow-sm">
            W
          </div>
          <div className="flex flex-col">
            <span className="font-heading font-bold text-lg sm:text-xl tracking-tight text-[#0f2b48] leading-tight">
              Wasatch Foot &amp; Ankle Center
            </span>
            <span className="text-xs text-slate-500 font-body">
              Spence D. Harper, DPM &bull; Board Certified
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-4 xl:gap-5 whitespace-nowrap">
          {navLinks.map((link) => {
            const isActive = !link.external && location === link.href;
            return link.external ? (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[13px] font-medium transition-colors hover:text-[#0284c7] text-slate-700"
                >
                  {link.title}
                </a>
              ) : (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`text-[13px] font-medium transition-colors hover:text-[#0284c7] ${
                    isActive ? "text-[#0284c7] font-bold" : "text-slate-700"
                  }`}
                >
                  {link.title}
                </Link>
              );
          })}
        </nav>

        {/* Primary CTA */}
        <div className="hidden sm:flex items-center gap-3">
          <Link
            href="/appointments/"
            className="inline-flex items-center gap-2 whitespace-nowrap bg-[#0284c7] hover:bg-[#0369a1] text-white text-[13px] font-semibold px-4 py-2.5 rounded-lg shadow-sm transition-all transform active:scale-95"
          >
            <Calendar className="w-4 h-4" />
            Request Appointment
          </Link>
        </div>

        {/* Mobile Hamburger Toggle */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle navigation menu"
          className="lg:hidden p-2 text-slate-700 hover:text-slate-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0284c7]"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Nav Drawer */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-slate-50 border-t border-slate-200 px-4 pt-3 pb-6 space-y-2">
          {navLinks.map((link) => {
            const isActive = !link.external && location === link.href;
            return link.external ? (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2 rounded-md text-base font-medium text-slate-800 hover:bg-slate-200"
                >
                  {link.title}
                </a>
              ) : (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    isActive
                      ? "bg-[#0f2b48] text-white"
                      : "text-slate-800 hover:bg-slate-200"
                  }`}
                >
                  {link.title}
                </Link>
              );
          })}
          <div className="pt-4 flex flex-col gap-2">
            <Link
              href="/appointments/"
              onClick={() => setMobileMenuOpen(false)}
              className="w-full text-center bg-[#0284c7] hover:bg-[#0369a1] text-white font-semibold py-3 px-4 rounded-lg shadow"
            >
              Request Appointment Online
            </Link>
            <a
              href={`tel:${PRACTICE_INFO.phone}`}
              className="w-full text-center bg-[#0f2b48] text-white font-semibold py-3 px-4 rounded-lg"
            >
              Call (435) 657-0329
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="bg-[#0b1d30] text-slate-300 pt-14 pb-10 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Practice Column */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded bg-[#0284c7] text-white font-bold flex items-center justify-center">
                W
              </div>
              <h3 className="font-heading text-lg font-bold text-white">
                Wasatch Foot &amp; Ankle
              </h3>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed font-body">
              Specialized podiatric medicine, board-certified foot and ankle surgery, and cutting-edge Swift microwave wart therapy serving Heber City, Midway, Park City, and Wasatch County.
            </p>
            <div className="flex items-center gap-2 text-xs text-cyan-300 font-medium">
              <ShieldCheck className="w-4 h-4" />
              <span>Board Certified by ABFAS</span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-3">
            <h4 className="font-heading text-sm font-semibold tracking-wider text-white uppercase">
              Quick Navigation
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link href="/" className="hover:text-white transition-colors">Home</Link>
              </li>
              <li>
                <Link href="/about/" className="hover:text-white transition-colors">Meet Dr. Spence Harper</Link>
              </li>
              <li>
                <Link href="/services/" className="hover:text-white transition-colors">Podiatry Services Overview</Link>
              </li>
              <li>
                <Link href="/services/swift-microwave-therapy/" className="hover:text-white transition-colors">Swift Microwave Wart Therapy</Link>
              </li>
              <li>
                <Link href="/heber-office/" className="hover:text-white transition-colors">Heber City Office</Link>
              </li>
              <li>
                <Link href="/appointments/" className="hover:text-white transition-colors">Request Appointment</Link>
              </li>
              <li>
                <Link href="/articles/" className="hover:text-white transition-colors">Foot &amp; Ankle Articles</Link>
              </li>
              <li>
                <Link href="/forms/" className="hover:text-white transition-colors">Patient Forms</Link>
              </li>
              <li>
                <a href="https://search.google.com/local/writereview?placeid=ChIJWfBvY7P1TYcR1cbImu6-QFU" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors inline-flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-amber-300" /> Rate Us on Google</a>
              </li>
            </ul>
          </div>

          {/* Core Treatments */}
          <div className="space-y-3">
            <h4 className="font-heading text-sm font-semibold tracking-wider text-white uppercase">
              Treatments
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link href="/services/swift-microwave-therapy/" className="hover:text-white transition-colors">
                  Swift Plantar Wart Treatment
                </Link>
              </li>
              <li>
                <Link href="/services/surgical-foot-ankle-corrections/" className="hover:text-white transition-colors">
                  Foot &amp; Ankle Surgery
                </Link>
              </li>
              <li>
                <Link href="/services/bunion-correction/" className="hover:text-white transition-colors">
                  Bunion Corrections
                </Link>
              </li>
              <li>
                <Link href="/services/heel-pain-plantar-fasciitis/" className="hover:text-white transition-colors">
                  Heel Pain &amp; Plantar Fasciitis
                </Link>
              </li>
              <li>
                <Link href="/services/ingrown-toenails/" className="hover:text-white transition-colors">
                  Ingrown Toenail Procedures
                </Link>
              </li>
              <li>
                <Link href="/services/mortons-neuroma-treatment/" className="hover:text-white transition-colors">
                  Morton's Neuroma Relief
                </Link>
              </li>
            </ul>
          </div>

          {/* Office Contact */}
          <div className="space-y-3">
            <h4 className="font-heading text-sm font-semibold tracking-wider text-white uppercase">
              Heber City Office
            </h4>
            <div className="text-sm space-y-2 text-slate-300">
              <p className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-cyan-400 mt-1 shrink-0" />
                <span>
                  190 N. Main Street<br />
                  Heber City, UT 84032
                </span>
              </p>
              <p className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-cyan-400 shrink-0" />
                <a href={`tel:${PRACTICE_INFO.phone}`} className="hover:text-white">
                  {PRACTICE_INFO.phone}
                </a>
              </p>
              <p className="text-xs text-slate-400">
                Fax: <span className="text-slate-300">{PRACTICE_INFO.fax}</span>
              </p>
              <div className="pt-2">
                <Link
                  href="/appointments/"
                  className="inline-block bg-[#0284c7] hover:bg-[#0369a1] text-white text-xs font-semibold px-4 py-2 rounded shadow transition-all"
                >
                  Schedule Appointment
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar with copyright, sitemap, and patient-form access */}
        <div className="pt-8 border-t border-slate-800 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-slate-400">
          <div>
            &copy; {new Date().getFullYear()} Wasatch Foot &amp; Ankle Center. All rights reserved.
          </div>
          <div className="flex items-center gap-6">
            <Link href="/sitemap.xml" className="hover:text-slate-300">Sitemap</Link>
            <Link href="/forms/" className="hover:text-slate-300">Privacy &amp; Patient Forms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function Breadcrumbs({ items }: { items: { label: string; href?: string }[] }) {
  return (
    <nav aria-label="Breadcrumb" className="bg-slate-50 border-b border-slate-200 py-2.5 px-4">
      <div className="max-w-7xl mx-auto flex items-center gap-2 text-xs sm:text-sm text-slate-600">
        <Link href="/" className="hover:text-[#0284c7] font-medium">
          Home
        </Link>
        {items.map((item, idx) => (
          <React.Fragment key={idx}>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
            {item.href ? (
              <Link href={item.href} className="hover:text-[#0284c7]">
                {item.label}
              </Link>
            ) : (
              <span className="text-[#0f2b48] font-semibold">{item.label}</span>
            )}
          </React.Fragment>
        ))}
      </div>
    </nav>
  );
}
